import { TaskAcceptedResponse } from '@luminovo/api-contracts';
import { analytics } from '@luminovo/data-stack';
import * as React from 'react';

import { useUploadFileToBomImporter } from '@/domain/bomImporter/bomImporterHandler';

export type UseBomImporterReturn = {
    bomImportState: {
        taskEndpoint: string | null;
        isUploading: boolean;
        bomFile: File | null;
        /** The assembly the upload is for. Only that assembly's importer picks it up. */
        assemblyId: string | null;
    };
    error: {
        message: string;
        isBOMErrorDialogOpen: boolean;
        setBOMErrorDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
        openBOMErrorDialogWithMessage: ({ message, assemblyId }: { message: string; assemblyId: string }) => void;
    };
    /**
     * Doesn't navigate: the dialogs live on the assembly page, so a caller elsewhere goes there (ImportBomButton).
     * Never rejects. Resolves to false when the upload failed, which the error dialog has already shown.
     */
    handleBomImport: ({
        files,
        rfqId,
        assemblyId,
    }: {
        files: File[];
        rfqId: string;
        assemblyId: string;
    }) => Promise<boolean>;
    bomImportTaskCleanUp: () => void;
};

const BomImporterContext = React.createContext<UseBomImporterReturn | undefined>(undefined);

export function BomImporterProvider({ children }: { children: React.ReactNode }) {
    const { mutateAsync: uploadFileToBomImporter } = useUploadFileToBomImporter();
    const [isUploading, setIsUploading] = React.useState<boolean>(false);

    const [bomErrorDialogIsOpen, setBomErrorDialogIsOpen] = React.useState<boolean>(false);
    const [bomErrorMessage, setBomErrorMessage] = React.useState<string>('');
    const [bomFile, setBomImporterFile] = React.useState<null | File>(null);
    const [taskEndpoint, setTaskEndpoint] = React.useState<string | null>(null);
    const [uploadAssemblyId, setUploadAssemblyId] = React.useState<string | null>(null);
    // Counts uploads, so one answered after a newer one started can't hand its task to the newer one's assembly.
    const latestUpload = React.useRef(0);

    const openBOMErrorDialogWithMessage = React.useCallback(
        ({ message, assemblyId }: { message: string; assemblyId: string }) => {
            analytics.track('could_not_upload_bom', { assembly_uuid: assemblyId, error_type: message });
            setBomErrorMessage(message);
            setBomErrorDialogIsOpen(true);
        },
        [],
    );

    const handleBomImport = React.useCallback(
        async ({ files, rfqId, assemblyId }: { files: File[]; rfqId: string; assemblyId: string }) => {
            analytics.track('initiate_bom_upload', { rfq_id: rfqId, assembly_uuid: assemblyId });
            if (files.length === 1) {
                const upload = ++latestUpload.current;
                setIsUploading(true);
                setUploadAssemblyId(assemblyId);
                // The previous upload's task, if the user left it running, is not this assembly's to resume.
                setTaskEndpoint(null);
                let taskAcceptedResponse: TaskAcceptedResponse;
                try {
                    taskAcceptedResponse = await uploadFileToBomImporter({
                        file: files[0],
                        assemblyId,
                        openBOMErrorDialogWithMessage,
                    });
                } catch {
                    // The mutation's onError has opened the error dialog. Rethrowing would only reach
                    // the global unhandledrejection handler, which Sentry reports as an unhandled error.
                    return false;
                }
                if (upload !== latestUpload.current) {
                    return true;
                }

                setBomImporterFile(files[0]);
                setTaskEndpoint(taskAcceptedResponse.task_endpoint);
            }
            return true;
        },
        [uploadFileToBomImporter, openBOMErrorDialogWithMessage],
    );

    const bomImportTaskCleanUp = React.useCallback(() => {
        setTaskEndpoint(null);
        setIsUploading(false);
        setUploadAssemblyId(null);
    }, []);

    const error = React.useMemo(
        () => ({
            message: bomErrorMessage,
            isBOMErrorDialogOpen: bomErrorDialogIsOpen,
            setBOMErrorDialogOpen: setBomErrorDialogIsOpen,
            openBOMErrorDialogWithMessage,
        }),
        [bomErrorMessage, bomErrorDialogIsOpen, openBOMErrorDialogWithMessage],
    );

    const bomImportState = React.useMemo(
        () => ({
            taskEndpoint,
            isUploading,
            bomFile,
            assemblyId: uploadAssemblyId,
        }),
        [taskEndpoint, isUploading, bomFile, uploadAssemblyId],
    );

    const contextValue = React.useMemo(
        () => ({ error, bomImportState, handleBomImport, bomImportTaskCleanUp }),
        [error, bomImportState, handleBomImport, bomImportTaskCleanUp],
    );

    return <BomImporterContext.Provider value={contextValue}>{children}</BomImporterContext.Provider>;
}

export function useBomImporter(): UseBomImporterReturn | undefined {
    return React.useContext(BomImporterContext);
}
